import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GetTasksAndAlertsHourlyActivityAnalyticsResponseData } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { AnalyticsQueryFilter } from '../util/analytics-query-filter';
import { FETCH_HOURLY_ACTIVITY_QUERY_KEY } from './analytics-query-keys';

export type FetchTasksAndAlertsHourlyActivityResult = {
    data: GetTasksAndAlertsHourlyActivityAnalyticsResponseData | undefined;
    status: 'error' | 'success' | 'pending';
    refetch: () => void;
};

/**
 * Tasks and alerts bucketed by hour of the week — seven days of twenty-four
 * counts, which the heatmap renders a cell apiece.
 */
export function useFetchTasksAndAlertsHourlyActivity(
    query: AnalyticsQueryFilter
): FetchTasksAndAlertsHourlyActivityResult {
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const { status, data, refetch } = useQuery({
        queryKey: [FETCH_HOURLY_ACTIVITY_QUERY_KEY, query],
        queryFn: () => {
            return openApiRef.current.analytics
                .getTasksAndAlertsHourlyActivity(query)
                .then((response) => response.data.data);
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: 0
    });

    return {
        status,
        data,
        refetch: () => {
            void refetch();
        }
    };
}
